import { QuillEditor } from "@pathwright/blocks/src/shared"
import { sansSerifStyles } from "@pathwright/blocks/src/ui/typography"
import { Content } from "@pathwright/ui/src/components/content/Content"
import useHoverCardOrPopover, {
  useHoverCardOrPopoverContext
} from "@pathwright/ux/src/components/chakra/useHoverCardOrPopover"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import PropTypes from "prop-types"
import React from "react"
import styled from "styled-components"
import { Button, IconButton, Box } from "@chakra-ui/react"
import RefKeyControl from "@pathwright/utils/src/keyboard/RefKeyControl"
import { isEmptyHTML } from "../../../utils"

const StyledWrapper = styled.div`
  textarea {
    outline: none;
    border: none;
    resize: none;
    width: 100%;
  }
`

const AnnotationTarget = styled.div`
  z-index: 5;
  position: absolute;
  width: 20px;
  height: 20px;
  box-shadow: 0 0 3px 3px rgba(0, 0, 0, 0.1);
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  transform: translate3d(-50%, -50%, 0);
  outline: none;
`

const AnnotationEditorContainer = styled(Box)`
  .ql-blank:before,
  .ql-editor > p {
    color: rgba(0, 0, 0, 0.6) !important;
    ${sansSerifStyles({ size: 18 })}
  }

  .ql-tooltip.ql-editing[data-mode="link"] .ql-tooltip-editor input {
    left: 0;
  }
`

const AnnotationCircle = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: #888888;
`

function DoneButton() {
  const popover = useHoverCardOrPopoverContext()

  return (
    <Button variant="outline" onClick={() => popover.setOpen(false)} mt={2}>
      Done
    </Button>
  )
}

function HandleEnterKey({ children }) {
  const popover = useHoverCardOrPopoverContext()

  return (
    <RefKeyControl
      keyControl={["Meta+Enter", "Control+Enter"]}
      handler={() => {
        popover.setOpen(false)
      }}
      options={{
        capture: true
      }}
    >
      {children}
    </RefKeyControl>
  )
}

function OpenPopoverWhenActive({ active }) {
  const popover = useHoverCardOrPopoverContext()

  React.useEffect(() => {
    if (active) {
      popover.setOpen(true)
    }
  }, [active])

  return null
}

const propsAreEqual = (prevProps, nextProps) => {
  if (prevProps.active !== nextProps.active) return false
  if (prevProps.build !== nextProps.build) return false
  if (prevProps.annotation !== nextProps.annotation) return false
  return false

  return true
}

const Annotation = React.memo(
  ({
    annotation,
    active,
    build,
    onChangeAnnotation,
    onShowAnnotation,
    onHideAnnotation,
    onRemoveAnnotation
  }) => {
    const HoverCardOrPopover = useHoverCardOrPopover()

    const isEmpty = isEmptyHTML(annotation.html)

    return (
      <StyledWrapper className="Annotation">
        <HoverCardOrPopover.Root
          lazyMount
          unmountOnExit // Mainly to reinstate the QuillEditor autofocus behavior.
          // Modify delays based on whether the annotation is empty or not.
          openDelay={isEmpty ? 0 : 200} // faster to edit
          closeDelay={isEmpty ? 600 : 200} // slower to exit
          skipAnimationOnMount={!!build}
          onOpenChange={(detail) => {
            // Delay hiding the popover merely to prevent the case where the user clicks outside
            // of the popover and would otherwise result in creating a new annotation (which we do not want).
            if (detail.open) {
              onShowAnnotation()
            } else {
              setTimeout(onHideAnnotation, 100)
            }
          }}
        >
          <OpenPopoverWhenActive active={active} />
          <HoverCardOrPopover.Trigger asChild>
            <AnnotationTarget
              className="AnnotationTarget"
              tabIndex="0"
              style={{
                left: annotation.xPos + "%",
                top: annotation.yPos + "%"
              }}
            >
              <AnnotationCircle className="AnnotationCircle" />
            </AnnotationTarget>
          </HoverCardOrPopover.Trigger>
          <HoverCardOrPopover.Positioner>
            <HoverCardOrPopover.Content minW="250px" p={4}>
              <HoverCardOrPopover.Arrow />
              {build ? (
                <AnnotationEditorContainer px={6}>
                  <HandleEnterKey>
                    <QuillEditor
                      className="AnnotationEditor"
                      alwaysEditable
                      autofocus={{ preventScroll: true }}
                      html={annotation.html}
                      onChange={(html) =>
                        onChangeAnnotation({
                          ...annotation,
                          html
                        })
                      }
                      onDelete={onRemoveAnnotation}
                    />
                  </HandleEnterKey>
                  <DoneButton />
                  <IconButton
                    aria-label="Delete annotation"
                    variant="ghost"
                    size="sm"
                    onClick={onRemoveAnnotation}
                    pos="absolute"
                    top={1}
                    right={1}
                  >
                    <Pathicon icon="trash" />
                  </IconButton>
                </AnnotationEditorContainer>
              ) : (
                <Content.Text
                  dangerouslySetInnerHTML={{
                    __html: annotation.html
                  }}
                />
              )}
            </HoverCardOrPopover.Content>
          </HoverCardOrPopover.Positioner>
        </HoverCardOrPopover.Root>
      </StyledWrapper>
    )
  },
  propsAreEqual
)

Annotation.propTypes = {
  annotation: PropTypes.shape({
    html: PropTypes.string.isRequired,
    xPos: PropTypes.number.isRequired,
    yPos: PropTypes.number.isRequired
  }).isRequired,
  onHideAnnotation: PropTypes.func.isRequired,
  onShowAnnotation: PropTypes.func.isRequired,
  onChangeAnnotation: PropTypes.func.isRequired,
  onRemoveAnnotation: PropTypes.func.isRequired
}

export default Annotation
